import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { FloatingActionButton } from '../../../models/common';
import { BackdropService } from '../../../services/backdrop/backdrop.service';
import { FloatingActionButtonService } from '../../../services/floating-action-button/floating-action-button.service';
import { HideOpenElementsService } from '../../../services/hide-open-elements/hide-open-elements.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';

@LifecycleAware()
@Component({
    selector: 'lsq-floating-action-button',
    template: `
        <div class="fab-container">
            <lsq-button
                *ngIf="isMobile && actions && actions.length === 1"
                class="floating-action-button"
                (buttonClick)="handleAction(actions[0].action, $event)"
                appearance="circle"
            >
                <lsq-icon
                    style="vertical-align: middle;"
                    [type]="actions[0].icon"
                    [color]="color.White"
                ></lsq-icon>
            </lsq-button>
            <lsq-button
                *ngIf="isMobile && actions && actions.length > 1"
                class="floating-action-button"
                [ngClass]="{ 'fab-open': fabOpen }"
                (buttonClick)="toggleFab()"
                appearance="circle"
            >
                <lsq-icon style="vertical-align: middle;" [type]="icons.Add" [color]="color.White">
                </lsq-icon>
            </lsq-button>

            <div
                *ngIf="isMobile && actions && actions.length > 1 && fabOpen"
                class="fab-buttons-container"
            >
                <div
                    class="floating-action-button-option"
                    *ngFor="let action of actions; let i = index"
                    [ngStyle]="{ bottom: getTopPosition(i) }"
                    [@slide]
                >
                    <lsq-button
                        (buttonClick)="handleAction(action.action, $event)"
                        appearance="circle-secondary"
                    >
                        <lsq-icon
                            style="vertical-align: middle;"
                            [type]="action.icon"
                            [color]="color.White"
                        >
                        </lsq-icon>
                    </lsq-button>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./lsq-floating-action-button.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('slide', [
            transition(':enter', [
                style({
                    bottom: 10
                }),
                animate(
                    '400ms cubic-bezier(0, 0.9, 0.2, 1)',
                    style({
                        bottom: '*'
                    })
                )
            ]),
            transition(':leave', [
                style({
                    bottom: '*'
                }),
                animate(
                    '400ms cubic-bezier(0, 0.9, 0.2, 1)',
                    style({
                        bottom: 10
                    })
                )
            ])
        ]),
        trigger('fadeIn', [
            transition(':enter', [
                style({
                    opacity: 0
                }),
                animate(
                    '200ms linear',
                    style({
                        opacity: '*'
                    })
                )
            ])
        ])
    ]
})
export class LsqFloatingActionButtonComponent implements OnInit, OnDestroy {
    public actions: FloatingActionButton[] = [];
    public icons = Icons;
    public formSubmitted = false;
    public color = Color;
    public isMobile = false;
    public fabOpen = false;

    constructor(
        private responsiveService: ResponsiveService,
        private floatingActionButtonService: FloatingActionButtonService,
        public backdropService: BackdropService,
        public hideOpenElementsService: HideOpenElementsService
    ) {}

    public ngOnInit(): void {
        this.responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
            this.isMobile = isMobile;
        });

        this.floatingActionButtonService.buttonsStream
            .pipe(forLifeOf(this))
            .subscribe((registeredActions: FloatingActionButton[]) => {
                this.actions = registeredActions;
            });

        this.hideOpenElementsService.hideOpenElementsStream.pipe(forLifeOf(this)).subscribe(() => {
            if (this.fabOpen) {
                this.toggleFab();
            }
        });
    }

    public ngOnDestroy(): void {}

    public handleAction(action: (event: Event) => void, event: Event): void {
        if (this.actions.length > 1) {
            this.toggleFab();
        }
        action(event);
        event.stopPropagation();
    }

    public toggleFab(): void {
        this.fabOpen = !this.fabOpen;
        if (this.fabOpen) {
            this.backdropService.openBackdrop({
                onClick: this.toggleFab.bind(this),
                isWhite: true,
                isBehindContent: true
            });
        } else {
            this.backdropService.closeBackDrop();
        }
    }

    public getTopPosition(index: number): string {
        return `${index * 6 + 8}rem`;
    }
}
